/* ============================================================================
   Admin module — generic settings form, bound to /api/manage/settings.
   Renders one field per Setting row (bool -> checkbox, int -> number, string ->
   text), tracks dirty values, and PUTs only changed keys. Used by both the
   Security policy and Notifications tabs (differ only by `category`).
   ============================================================================ */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPut, isApiError } from '../../lib/api';
import type { AdminSetting } from './types';

interface Props {
  category: 'security' | 'notifications';
  title: string;
  intro?: string;
}

export default function SettingsPanel({ category, title, intro }: Props) {
  const [settings, setSettings] = useState<AdminSetting[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const rows = await apiGet<AdminSetting[]>(`/api/manage/settings?category=${category}`);
      setSettings(rows);
      setValues(Object.fromEntries(rows.map((r) => [r.key, r.value])));
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = useMemo(
    () => settings.filter((s) => values[s.key] !== s.value),
    [settings, values],
  );

  function set(key: string, value: string) {
    setSaved(false);
    setValues((v) => ({ ...v, [key]: value }));
  }

  async function save() {
    if (dirty.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await apiPut('/api/manage/settings', {
        settings: dirty.map((s) => ({ key: s.key, value: values[s.key] })),
      });
      setSaved(true);
      await load();
    } catch (e) {
      setError(isApiError(e) ? e.message : 'Could not save settings.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="card" style={{ display: 'grid', placeItems: 'center', padding: 48 }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="card card-pad-lg" style={{ maxWidth: 680 }}>
      <div className="card-header">
        <div>
          <h3>{title}</h3>
          {intro && <p className="muted" style={{ margin: '4px 0 0' }}>{intro}</p>}
        </div>
      </div>

      {error && (
        <div className="error-text" style={{ marginBottom: 14 }}>
          {error}
        </div>
      )}

      {settings.length === 0 ? (
        <p className="muted">No settings available.</p>
      ) : (
        <div className="stack" style={{ gap: 4 }}>
          {settings.map((s) =>
            s.valueType === 'bool' ? (
              <label
                key={s.key}
                className="row"
                style={{
                  gap: 12,
                  alignItems: 'flex-start',
                  padding: '12px 0',
                  borderBottom: '1px solid var(--line)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={values[s.key] === 'true'}
                  onChange={(e) => set(s.key, e.target.checked ? 'true' : 'false')}
                  style={{ width: 18, height: 18, marginTop: 2, accentColor: 'var(--uva-navy)' }}
                />
                <span>
                  <span style={{ fontWeight: 600, color: 'var(--ink-2)', fontSize: 'var(--fs-sm)' }}>
                    {s.name}
                  </span>
                  {s.description && (
                    <span className="muted" style={{ display: 'block', fontSize: 'var(--fs-xs)' }}>
                      {s.description}
                    </span>
                  )}
                </span>
              </label>
            ) : (
              <div key={s.key} className="field" style={{ marginBottom: 12 }}>
                <label htmlFor={`set-${s.key}`}>{s.name}</label>
                <input
                  id={`set-${s.key}`}
                  className="in"
                  style={{ maxWidth: 200 }}
                  type={s.valueType === 'int' ? 'number' : 'text'}
                  min={s.valueType === 'int' ? 0 : undefined}
                  value={values[s.key] ?? ''}
                  onChange={(e) => set(s.key, e.target.value)}
                />
                {s.description && <span className="hint">{s.description}</span>}
              </div>
            ),
          )}
        </div>
      )}

      <div className="row" style={{ gap: 12, alignItems: 'center', marginTop: 18 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving || dirty.length === 0}>
          {saving
            ? 'Saving…'
            : dirty.length > 0
              ? `Save ${dirty.length} change${dirty.length > 1 ? 's' : ''}`
              : 'Save changes'}
        </button>
        {saved && dirty.length === 0 && <span className="pill pill-success">Saved</span>}
      </div>
    </div>
  );
}
